import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * An add/edit form in a modal: centered on wide screens, a bottom sheet on
 * narrow ones. The header and footer stay put while the fields scroll. Its
 * submit opens the change's `ConfirmDialog`, which stacks above it.
 */
export function FormDialog({
  title,
  label,
  dirty,
  busy,
  problem,
  onSubmit,
  onClose,
  children,
}: {
  title: ReactNode;
  /** The form's accessible name. */
  label: string;
  /** Unsaved edits: closing asks before discarding them. */
  dirty: boolean;
  busy: boolean;
  /** Why the draft can't be submitted yet. */
  problem: string | null;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  // Mounted only while open; unmounting removes it from the top layer.
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const requestClose = () => {
    if (busy) return;
    if (dirty && !window.confirm('Discard your changes?')) return;
    onClose();
  };

  return (
    <dialog
      ref={ref}
      data-form-dialog
      aria-labelledby={titleId}
      // Escape asks too, rather than dropping edits silently.
      onCancel={event => {
        event.preventDefault();
        requestClose();
      }}
      className="pane m-auto w-[52rem] max-w-[calc(100vw-2rem)] max-h-[85vh] p-0 text-ink shadow-lg backdrop:bg-black/35 open:flex open:flex-col max-md:mx-0 max-md:mt-auto max-md:mb-0 max-md:max-h-[92dvh] max-md:w-full max-md:max-w-full max-md:rounded-t-xl max-md:border-b-0"
    >
      <form
        aria-label={label}
        // The forms' own checks give clearer reasons than the browser's.
        noValidate
        // `flex-auto`, never `flex-1`: the dialog's height comes from its
        // content, and WebKit (iOS Safari) collapses a 0% flex basis in an
        // auto-height column to nothing, leaving only the backdrop showing.
        // The body still shrinks to the height cap and scrolls (min-h-0).
        className="flex min-h-0 flex-auto flex-col"
        onSubmit={event => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 id={titleId} className="text-sm font-semibold">
            {title}
          </h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-icon"
            aria-label="Close"
            disabled={busy}
            onClick={requestClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="min-h-0 flex-auto overflow-y-auto px-4 pb-4 text-xs text-ink-2">
          {children}
        </div>
        <div className="shrink-0 border-t border-line px-4 py-3">
          {problem && <p className="callout callout-err mb-3">{problem}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              Review…
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={requestClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </dialog>
  );
}
